<template>
  <!-- 계정관리 모달 — SPEC-CHINA-ADMIN-HOSPITAL-001 REQ-ACC-001~005 -->
  <a-modal
    :open="open"
    :title="$t('company_acc_modal_title')"
    width="1120px"
    wrap-class-name="acc-modal-responsive"
    :footer="null"
    :closable="true"
    :mask-closable="false"
    :keyboard="false"
    :centered="true"
    :destroy-on-close="true"
    @cancel="onCancel"
  >
    <!-- 로딩 상태 -->
    <div v-if="loadingAccounts" style="text-align: center; padding: 24px;">
      <a-spin />
    </div>

    <template v-else>
      <!-- 에러 표시 -->
      <a-alert
        v-if="errorMsg"
        type="error"
        :message="errorMsg"
        style="margin-bottom: 12px;"
        show-icon
        closable
        @close="errorMsg = ''"
      />

      <!-- 계정 목록 테이블 — 모달이 96vw/calc(100vw-24px)로 클램프되므로 x-스크롤 확보
           (username+name+role+isFirstLogin 자동폭 ~4×110 + lastLogin 200 + status 100 + actions 220 ≈ 960) -->
      <a-table
        :data-source="accounts"
        :columns="accountColumns"
        :pagination="false"
        row-key="_id"
        size="small"
        style="margin-bottom: 16px;"
        :scroll="{ x: 1000 }"
      >
        <template #title>
          <LockPolicyHint />
        </template>
        <template #bodyCell="{ column, record }">
          <!-- isFirstLogin 표시 -->
          <template v-if="column.key === 'isFirstLogin'">
            <a-tag :color="record.isFirstLogin === 'Y' ? 'orange' : 'green'">
              {{ record.isFirstLogin === 'Y' ? $t('company_acc_firstLogin_yes') : $t('company_acc_firstLogin_no') }}
            </a-tag>
          </template>

          <!-- F2: 마지막 접속 정보 -->
          <template v-if="column.key === 'lastLogin'">
            <span style="font-size: 12px; color: #666; white-space: nowrap;">
              {{ formatLastLogin(record) }}
            </span>
          </template>

          <!-- F1: 사용중지 상태 표시 — useYn==='N'이면 사용중지 (SPEC-CHINA-IAM-ACCOUNT-LIFECYCLE-001) -->
          <template v-if="column.key === 'status'">
            <a-tag v-if="record.useYn === 'N'" color="red">
              {{ $t('company_acc_status_suspended') }}
            </a-tag>
            <a-tag v-else color="green">
              {{ $t('company_acc_status_active') }}
            </a-tag>
            <!-- F5: 잠김 상태 — lockedUntil 미래면 잠김 + 남은 분 표시 -->
            <a-tooltip v-if="isLocked(record)" :title="$t('acc_lock_failed_count', { n: record.failedLoginCount || 0 })">
              <a-tag color="volcano">{{ $t('acc_locked_tag', { n: lockRemainingMinutes(record) }) }}</a-tag>
            </a-tooltip>
          </template>

          <!-- 행 액션 — [2026-08-04] 버튼 7개가 셀 안에서 줄바꿈되며 깨져 자주 쓰는 2개만 남기고
               나머지는 "더보기" 드롭다운으로 이동. 확인창은 popconfirm 대신 twoBtnDialog
               (드롭다운 메뉴가 닫히면서 popconfirm 앵커가 사라지는 조합 문제 회피). -->
          <template v-if="column.key === 'actions'">
            <a-space>
              <!-- 메뉴편집: 기존 MenuModal 재사용 (REQ-ACC-005) -->
              <a-button size="small" @click="openMenuModal(record)">
                {{ $t('company_acc_btn_menu') }}
              </a-button>
              <!-- 수정: name만 (OD-FE-4, REQ-ACC-003) -->
              <a-button size="small" @click="openUpdateModal(record)">
                {{ $t('company_acc_btn_edit') }}
              </a-button>
              <a-dropdown :trigger="['click']">
                <a-button
                  size="small"
                  :loading="statusChangingId === record._id || deletingId === record._id"
                >
                  {{ $t('company_acc_btn_more') }} ▾
                </a-button>
                <template #overlay>
                  <a-menu @click="({ key }) => onMoreAction(key, record)">
                    <a-menu-item key="resetPwd">{{ $t('company_acc_btn_resetPwd') }}</a-menu-item>
                    <a-menu-item key="forceLogout">{{ $t('acc_btn_force_logout') }}</a-menu-item>
                    <a-menu-item key="unlock">{{ $t('acc_btn_unlock') }}</a-menu-item>
                    <!-- 사용중지/재개 토글 — 본인 계정은 조작 불가 -->
                    <a-menu-item v-if="record.useYn === 'N'" key="activate" :disabled="isSelf(record)">
                      {{ $t('company_acc_btn_activate') }}
                    </a-menu-item>
                    <a-menu-item v-else key="suspend" danger :disabled="isSelf(record)">
                      {{ $t('company_acc_btn_suspend') }}
                    </a-menu-item>
                    <a-menu-divider />
                    <a-menu-item key="delete" danger :disabled="isSelf(record)">
                      {{ $t('company_acc_btn_delete') }}
                    </a-menu-item>
                  </a-menu>
                </template>
              </a-dropdown>
            </a-space>
          </template>
        </template>
      </a-table>

      <!-- 계정 추가 -->
      <div style="border-top: 1px solid #f0f0f0; padding-top: 12px;">
        <div style="font-weight: 600; margin-bottom: 8px;">{{ $t('company_acc_create_title') }}</div>

        <!-- [2026-08-03] `:model` 이 없어 antd 가 입력값을 읽지 못했고, 값을 채워도
             "请输入用户名/姓名" 이 계속 떠 있었다. Form.Item 은 `name` 으로 **model 에서**
             값을 찾는데 그 model 이 없으니 언제나 빈 값으로 판정된 것이다.

             종전 주석은 ":model 을 붙이면 Enter 가 @keyup.enter 와 겹쳐 2번 발동한다"였다.
             맞는 관찰이지만 해법이 반대였다 — model 을 빼는 대신 **Enter 처리를 폼에 넘긴다.**
             submit 버튼이 있으면 antd 가 Enter 를 받아 검증 후 @finish 를 한 번만 부른다. -->
        <a-form class="acc-create-form" :model="createForm" layout="inline" @finish="onCreateAccount">
          <a-form-item
            :label="$t('company_acc_username_label')"
            name="username"
            :rules="[{ required: true, message: $t('company_acc_username_required') }]"
          >
            <a-input
              v-model:value="createForm.username"
              :placeholder="$t('company_acc_username_placeholder')"
              class="acc-create-input"
              style="width: 200px;"
            />
          </a-form-item>
          <!-- REQ-ACC-002: backend DTO는 username+name 필수 (기존 ManuAccManage 계정생성 패턴 정합) -->
          <a-form-item
            :label="$t('company_acc_name_label')"
            name="name"
            :rules="[{ required: true, message: $t('company_acc_name_required') }]"
          >
            <a-input
              v-model:value="createForm.name"
              :placeholder="$t('company_acc_name_placeholder')"
              class="acc-create-input"
              style="width: 200px;"
            />
          </a-form-item>
          <a-form-item>
            <a-button
              type="primary"
              html-type="submit"
              :loading="creatingAccount"
            >
              {{ $t('company_acc_btn_add') }}
            </a-button>
          </a-form-item>
        </a-form>

        <!-- 계정 추가 에러 (409 포함) -->
        <a-alert
          v-if="createErrorMsg"
          type="error"
          :message="createErrorMsg"
          style="margin-top: 8px;"
          show-icon
          closable
          @close="createErrorMsg = ''"
        />
      </div>
    </template>

    <!-- 계정 수정 모달 (name만 — REQ-ACC-003) — 같은 화면 오버레이 클래스 재사용 -->
    <a-modal
      :open="updateModalOpen"
      :title="$t('company_acc_update_title')"
      width="400px"
      wrap-class-name="acc-modal-responsive"
      :footer="null"
      :centered="true"
      :destroy-on-close="true"
      @cancel="closeUpdateModal"
    >
      <!-- 생성 폼과 같은 함정: `name` 만 있고 model 이 없으면 Form 이 값을 못 본다.
           지금은 rules 가 없어 증상이 없지만, 나중에 필수검증을 붙이는 순간 같은 결함이 된다.
           model 을 붙여 두면 그때 그냥 동작한다. -->
      <a-form :model="editForm" layout="vertical">
        <a-form-item :label="$t('company_acc_name_label')" name="name">
          <a-input
            v-model:value="editForm.name"
            :placeholder="$t('company_acc_name_placeholder')"
          />
        </a-form-item>
      </a-form>
      <a-alert
        v-if="updateErrorMsg"
        type="error"
        :message="updateErrorMsg"
        style="margin-bottom: 8px;"
        show-icon
      />
      <div style="display: flex; justify-content: flex-end; gap: 8px;">
        <a-button @click="closeUpdateModal">{{ $t('company_acc_btn_cancel') }}</a-button>
        <a-button type="primary" :loading="updatingAccount" @click="onUpdateAccount">
          {{ $t('company_acc_btn_save') }}
        </a-button>
      </div>
    </a-modal>

    <!-- MenuModal 재사용 (REQ-ACC-005, EXC-9) -->
    <MenuModal
      :show-modal="menuModalOpen"
      :username="menuModalUsername"
      :target-role="menuModalRole"
      :copy-source-accounts="accounts"
      @close-modal="closeMenuModal"
    />
  </a-modal>
</template>

<script>
import MenuModal from '@/views/ManuAccManage/components/MenuModal.vue';
import LockPolicyHint from '@/components/LockPolicyHint.vue';
import {
  listAccounts,
  createAccount,
  updateAccount,
  resetAccountPassword,
  forceLogoutAccount,
  unlockAccount,
  deleteAccount,
  setAccountStatus,
  extractApiError,
} from '@/apis/chinaCompany';
import { twoBtnDialog, jwtUsername } from '@/utils/utils';
import dayjs from 'dayjs';

export default {
  name: 'AccountManageModal',

  components: { MenuModal, LockPolicyHint },

  props: {
    // @MX:NOTE: [AUTO] REQ-UNW-006 — class/style props 미정의
    open: {
      type: Boolean,
      default: false,
    },
    /** HOSPITAL | DENTAL_LABORATORY */
    type: {
      type: String,
      required: true,
    },
    /** 대상 회사 record (_id 필수) */
    company: {
      type: Object,
      default: null,
    },
  },

  emits: ['close'],

  data() {
    return {
      accounts: [],
      loadingAccounts: false,
      errorMsg: '',

      // 계정 추가
      // antd Form 은 `name` 으로 **이 객체에서** 값을 찾는다. 평평한 data 프로퍼티를
      // 쓰면 Form.Item 이 값을 못 봐 항상 '비어 있음'으로 검증된다(2026-08-03 실측 결함).
      createForm: {
        username: '',
        name: '',
      },
      creatingAccount: false,
      createErrorMsg: '',

      // 계정 수정
      updateModalOpen: false,
      editTarget: null,
      editForm: {
        name: '',
      },
      updatingAccount: false,
      updateErrorMsg: '',

      // MenuModal
      menuModalOpen: false,
      menuModalUsername: '',
      // 편집 대상 계정의 role — 메뉴 카탈로그 피커 필터에 사용(2026-07-30)
      menuModalRole: '',

      // F1: 사용중지/재개 · 삭제 진행중 표시(행별 loading)
      statusChangingId: '',
      deletingId: '',
    };
  },

  computed: {
    accountColumns() {
      return [
        { title: this.$t('company_acc_col_username'), dataIndex: 'username', key: 'username' },
        { title: this.$t('company_acc_col_name'), dataIndex: 'name', key: 'name' },
        { title: this.$t('company_acc_col_role'), dataIndex: 'role', key: 'role',
          customRender: ({ record }) => {
            const r = record.role;
            if (!r) return '-';
            // 역할 코드를 로케일 라벨로 변환(미정의 코드는 코드 그대로 표시)
            return this.$te(`company_role_${r}`) ? this.$t(`company_role_${r}`) : r;
          },
        },
        { title: this.$t('company_acc_col_isFirstLogin'), dataIndex: 'isFirstLogin', key: 'isFirstLogin' },
        // F2: 마지막 접속 정보
        { title: this.$t('acc_col_last_login'), key: 'lastLogin', width: 200 },
        // F1: 사용중지/재개 상태 표시
        { title: this.$t('company_acc_col_status'), key: 'status', width: 100 },
        { title: this.$t('company_acc_col_actions'), key: 'actions', width: 220 },
      ];
    },

    // F1: 로그인 계정 자기 자신 판별 — 본인 행은 사용중지/삭제 불가 (jwtUsername()은 self 판별 전용 헬퍼)
    isSelf() {
      return (record) => record.username === jwtUsername();
    },

    // F2: 마지막 접속 포맷 헬퍼
    formatLastLogin() {
      return (record) => {
        if (!record.lastLoginAt) return '-';
        const date = dayjs(record.lastLoginAt).format('YYYY-MM-DD HH:mm');
        const parts = [date];
        if (record.lastLoginIp) parts.push(record.lastLoginIp);
        const browser = record.lastLoginBrowser || record.lastLoginOs;
        if (browser) parts.push(browser);
        return parts.join(' · ');
      };
    },
  },

  watch: {
    open(val) {
      if (val && this.company) {
        this.fetchAccounts();
        this.createForm.username = '';
        this.createForm.name = '';
        this.createErrorMsg = '';
        this.errorMsg = '';
      }
    },
    company(val) {
      if (val && this.open) {
        this.fetchAccounts();
      }
    },
  },

  methods: {
    async fetchAccounts() {
      if (!this.company?._id) return;
      this.loadingAccounts = true;
      this.errorMsg = '';
      try {
        const res = await listAccounts(this.type, this.company._id);
        // 백엔드 sanitize로 password/refreshToken/initPwd 없음 — REQ-ACC-001
        this.accounts = Array.isArray(res.data) ? res.data : [];
      } catch (err) {
        const apiErr = extractApiError(err);
        this.errorMsg = apiErr.status === 403
          ? this.$t('company_error_403')
          : apiErr.message || this.$t('company_error_fetch_failed');
      } finally {
        this.loadingAccounts = false;
      }
    },

    // 계정 추가 — REQ-ACC-002 (409 UX 필수)
    async onCreateAccount() {
      // antd 필수검증을 통과해도 공백만 입력한 경우가 남는다 — trim 판정은 유지한다.
      if (!this.createForm.username.trim()) {
        this.createErrorMsg = this.$t('company_acc_username_required');
        return;
      }
      if (!this.createForm.name.trim()) {
        this.createErrorMsg = this.$t('company_acc_name_required');
        return;
      }
      this.creatingAccount = true;
      this.createErrorMsg = '';
      try {
        await createAccount(this.type, this.company._id, {
          username: this.createForm.username.trim(),
          name: this.createForm.name.trim(),
        });
        this.createForm.username = '';
        this.createForm.name = '';
        await this.fetchAccounts();
      } catch (err) {
        const apiErr = extractApiError(err);
        // @MX:NOTE: [AUTO] REQ-ACC-002: 409 = 전역 username 중복 UX (must-pass AC-ACC-CREATE-DUP-001)
        if (apiErr.status === 409) {
          this.createErrorMsg = this.$t('company_acc_error_dup_username');
        } else if (apiErr.status === 403) {
          this.createErrorMsg = this.$t('company_error_403');
        } else {
          this.createErrorMsg = apiErr.message || this.$t('company_error_save_failed');
        }
      } finally {
        this.creatingAccount = false;
      }
    },

    // 수정 모달 열기
    openUpdateModal(record) {
      this.editTarget = record;
      this.editForm.name = record.name || '';
      this.updateErrorMsg = '';
      this.updateModalOpen = true;
    },

    closeUpdateModal() {
      this.updateModalOpen = false;
      this.editTarget = null;
      this.editForm.name = '';
    },

    // 계정 수정 제출 — REQ-ACC-003: name만
    async onUpdateAccount() {
      if (!this.editTarget) return;
      this.updatingAccount = true;
      this.updateErrorMsg = '';
      try {
        await updateAccount(this.type, this.company._id, this.editTarget._id, {
          name: this.editForm.name,
        });
        this.closeUpdateModal();
        await this.fetchAccounts();
      } catch (err) {
        const apiErr = extractApiError(err);
        this.updateErrorMsg = apiErr.status === 403
          ? this.$t('company_error_403')
          : apiErr.message || this.$t('company_error_save_failed');
      } finally {
        this.updatingAccount = false;
      }
    },

    // 비밀번호 초기화 — REQ-ACC-004
    async onResetPassword(record) {
      const confirmed = await twoBtnDialog(
        this.$t('company_acc_resetPwd_confirm_title'),
        this.$t('company_acc_resetPwd_confirm_text'),
        this.$t('company_acc_btn_resetPwd'),
        this.$t('company_acc_btn_cancel'),
      );
      if (!confirmed) return;

      try {
        // 초기화: username을 newPassword로 사용 (backend가 isFirstLogin=Y로 재설정 — REQ-ACC-004)
        await resetAccountPassword(this.type, this.company._id, record._id, {
          newPassword: record.username,
        });
        this.$message.success(this.$t('company_acc_resetPwd_success'));
        await this.fetchAccounts();
      } catch (err) {
        const apiErr = extractApiError(err);
        this.$message.error(
          apiErr.status === 403
            ? this.$t('company_error_403')
            : apiErr.message || this.$t('company_error_save_failed'),
        );
      }
    },

    // F3/F4: 강제 로그아웃 — POST /china/{hospitals|dental-labs}/:companyId/accounts/:accountId/force-logout
    async onForceLogout(record) {
      const confirmed = await twoBtnDialog(
        this.$t('acc_force_logout_confirm_title'),
        this.$t('acc_force_logout_confirm_text'),
        this.$t('acc_btn_force_logout'),
        this.$t('company_acc_btn_cancel'),
      );
      if (!confirmed) return;

      try {
        await forceLogoutAccount(this.type, this.company._id, record._id);
        this.$message.success(this.$t('acc_force_logout_success'));
      } catch (err) {
        const apiErr = extractApiError(err);
        this.$message.error(apiErr.message || this.$t('acc_force_logout_error'));
      }
    },

    // F5: 잠김 판정 — lockedUntil 이 미래인 동안만 잠김 (클라이언트 시계 기준)
    isLocked(record) {
      return !!record.lockedUntil && new Date(record.lockedUntil).getTime() > Date.now();
    },

    lockRemainingMinutes(record) {
      if (!this.isLocked(record)) return 0;
      return Math.ceil((new Date(record.lockedUntil).getTime() - Date.now()) / 60000);
    },

    // F5: 로그인 잠금 해제 — POST /china/{hospitals|dental-labs}/:companyId/accounts/:accountId/unlock
    // 세션·비밀번호 무변경. 5회 실패 15분 잠금을 즉시 푼다.
    async onUnlock(record) {
      const confirmed = await twoBtnDialog(
        this.$t('acc_unlock_confirm_title'),
        this.$t('acc_unlock_confirm_text'),
        this.$t('acc_btn_unlock'),
        this.$t('company_acc_btn_cancel'),
      );
      if (!confirmed) return;

      try {
        await unlockAccount(this.type, this.company._id, record._id);
        this.$message.success(this.$t('acc_unlock_success'));
        await this.fetchAccounts();
      } catch (err) {
        const apiErr = extractApiError(err);
        this.$message.error(apiErr.message || this.$t('acc_unlock_error'));
      }
    },

    // 더보기 드롭다운 라우팅 — 파괴적 액션은 twoBtnDialog 로 확인 후 실행
    async onMoreAction(key, record) {
      if (key === 'resetPwd') return this.onResetPassword(record);
      if (key === 'forceLogout') return this.onForceLogout(record);
      if (key === 'unlock') return this.onUnlock(record);
      if (key === 'activate') {
        const ok = await twoBtnDialog(
          this.$t('company_acc_activate_confirm_title'),
          '',
          this.$t('company_acc_btn_activate'),
          this.$t('company_acc_btn_cancel'),
        );
        if (ok) await this.onToggleAccountStatus(record, true);
        return;
      }
      if (key === 'suspend') {
        const ok = await twoBtnDialog(
          this.$t('company_acc_suspend_confirm_title'),
          this.$t('company_acc_suspend_confirm_text'),
          this.$t('company_acc_btn_suspend'),
          this.$t('company_acc_btn_cancel'),
        );
        if (ok) await this.onToggleAccountStatus(record, false);
        return;
      }
      if (key === 'delete') {
        const ok = await twoBtnDialog(
          this.$t('company_acc_delete_confirm_title'),
          this.$t('company_acc_delete_confirm_text'),
          this.$t('company_acc_btn_delete'),
          this.$t('company_acc_btn_cancel'),
        );
        if (ok) await this.onDeleteAccount(record);
      }
    },

    // F1: 사용중지/재개 토글 — SPEC-CHINA-IAM-ACCOUNT-LIFECYCLE-001
    async onToggleAccountStatus(record, active) {
      this.statusChangingId = record._id;
      try {
        await setAccountStatus(this.type, this.company._id, record._id, active);
        this.$message.success(
          active ? this.$t('company_acc_activate_success') : this.$t('company_acc_suspend_success'),
        );
        await this.fetchAccounts();
      } catch (err) {
        const apiErr = extractApiError(err);
        this.$message.error(
          apiErr.status === 403
            ? this.$t('company_error_403')
            : apiErr.message || this.$t('company_error_save_failed'),
        );
      } finally {
        this.statusChangingId = '';
      }
    },

    // F1: 계정 삭제 — SPEC-CHINA-IAM-ACCOUNT-LIFECYCLE-001
    async onDeleteAccount(record) {
      this.deletingId = record._id;
      try {
        await deleteAccount(this.type, this.company._id, record._id);
        this.$message.success(this.$t('company_acc_delete_success'));
        await this.fetchAccounts();
      } catch (err) {
        const apiErr = extractApiError(err);
        this.$message.error(
          apiErr.status === 403
            ? this.$t('company_error_403')
            : apiErr.message || this.$t('company_error_save_failed'),
        );
      } finally {
        this.deletingId = '';
      }
    },

    // MenuModal 열기 — REQ-ACC-005 (기존 MenuModal 재사용)
    openMenuModal(record) {
      this.menuModalUsername = record.username;
      this.menuModalRole = record.role || '';
      this.menuModalOpen = true;
    },

    closeMenuModal() {
      this.menuModalOpen = false;
      this.menuModalUsername = '';
      this.menuModalRole = '';
    },

    onCancel() {
      this.$emit('close');
    },
  },
};
</script>

<style>
/* 계정관리 모달 — 10인치+ 에서 액션 컬럼(더보기 등)이 잘리지 않게 넓히되,
   작은 화면에선 96vw 로 축소해 가로 스크롤을 피한다. modal 은 teleport 되므로 non-scoped. */
.acc-modal-responsive .ant-modal {
  max-width: 96vw;
}

/* 모바일(≤767px) — 앱헤더(z-index 1020)에 가려지지 않도록 루트를 올리고, 뷰포트에 맞춰
   클램프한다. house precedent: IamConsole.vue 하단 예시 그대로 이관. */
@media (max-width: 767px) {
  .ant-modal-wrap.acc-modal-responsive {
    z-index: 1100;
  }
  .ant-modal-wrap.acc-modal-responsive ~ .ant-modal-mask {
    z-index: 1100;
  }
  .acc-modal-responsive .ant-modal {
    max-width: calc(100vw - 24px);
    margin: 8px auto;
  }
  .acc-modal-responsive .ant-modal-body {
    max-height: 70vh;
    max-height: 70dvh;
    overflow-y: auto;
  }

  /* 계정 추가 폼 — 200px 고정폭 입력 2개가 클램프된 모달 폭을 넘으므로 전량 100%로 쌓는다. */
  .acc-modal-responsive .acc-create-form .ant-form-item {
    width: 100%;
  }
  .acc-modal-responsive .acc-create-input {
    width: 100% !important;
  }
}
</style>
